// src/pages/StoreMode.js
import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { auth, db, functions } from "../firebase";
import {
  doc,
  getDoc,
  getDocs,
  collection,
  query,
  where,
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { useSchool } from "../hooks/useSchool";
import './StoreMode.css';

function extractSchoolIdFromPath(pathname = "") {
  const m = pathname.match(/^\/([^/]+)(?:\/|$)/);
  return m ? m[1] : null;
}

function parseScannedText(text) {
  // Accept raw IDs or JSON payloads like: {"itemId":"abc","schoolId":"nlcs"} or {"id":"abc"}
  try {
    const obj = JSON.parse(text);
    if (obj && (obj.itemId || obj.id || obj.studentId)) {
      return {
        itemId: obj.itemId || obj.id || null,
        studentId: obj.studentId || obj.id || null,
        embeddedSchoolId: obj.schoolId || null,
      };
    }
  } catch (_) {}
  return { itemId: text.trim(), studentId: text.trim(), embeddedSchoolId: null };
}

const StoreMode = () => {
  const [authChecked, setAuthChecked] = useState(false);
  const [student, setStudent] = useState(null);
  const [advisoryLabels, setAdvisoryLabels] = useState({});
  const [cart, setCart] = useState([]);
  const [message, setMessage] = useState("");
  const [lastTransaction, setLastTransaction] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const schoolId = useSchool();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        const currentUrl = window.location.pathname + window.location.search;
        navigate(`/login?redirect=${encodeURIComponent(currentUrl)}`);
        return;
      }
      // Load labels from this school's namespace. NLCS retains a temporary
      // fallback while its original shared totals documents are migrated.
      try {
        const labelsRef = doc(db, "schools", schoolId, "totals", "classLabels");
        let labelsSnap = await getDoc(labelsRef);
        if (!labelsSnap.exists()) {
          labelsSnap = await getDoc(
            doc(db, "schools", schoolId, "totals", "advisoryLabels")
          );
        }
        if (!labelsSnap.exists() && schoolId === "nlcs") {
          labelsSnap = await getDoc(doc(db, "totals", "advisoryLabels"));
        }
        if (labelsSnap.exists()) setAdvisoryLabels(labelsSnap.data());
      } catch (e) {
        console.warn("advisoryLabels read failed", e);
      }
      setAuthChecked(true);
    });
    return () => unsub();
  }, [navigate, schoolId]);
  async function handleSearchStudents(term) {
    setSearchTerm(term);
  
    if (!term || term.length < 2) {
      setSearchResults([]);
      return;
    }
  
    try {
      const q = query(
        collection(db, "users"),
        where("schoolId", "==", schoolId)
      );
  
      const snap = await getDocs(q);
  
      const results = snap.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .filter(u =>
          u.role === "student" &&
          u.name?.toLowerCase().includes(term.toLowerCase())
        );
  
      setSearchResults(results);
    } catch (err) {
      console.error("Search failed:", err);
    }
  }
  // Auto-load student from ?id=
  useEffect(() => {
    const studentIdFromQR = searchParams.get("id");
    if (authChecked && studentIdFromQR && !student) {
      loadStudentById(studentIdFromQR);
    }
  }, [authChecked, searchParams, student]);

  // Auto-load item from ?item=
  useEffect(() => {
    const itemIdFromQR = searchParams.get("item");
    if (authChecked && itemIdFromQR && student) {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.delete("item");
      setSearchParams(nextParams, { replace: true });
      loadItemToCart(itemIdFromQR);
    }
  }, [authChecked, searchParams, setSearchParams, student]);

  const getAdvisoryDisplayName = (adv) => advisoryLabels[adv] || adv;

  async function loadStudentById(idOrJson) {
    try {
      const { studentId } = parseScannedText(idOrJson);
      const id = studentId;

      const q = query(
        collection(db, "users"),
        where("uid", "==", id),
        where("schoolId", "==", schoolId)
      );
      
      const snap = await getDocs(q);
      
      if (snap.empty) {
        alert("Student not found");
        return;
      }
      
      const studentDoc = snap.docs[0];
      const studentData = studentDoc.data();

      if (!schoolId) {
        alert("No school context detected. Use your school URL (e.g. /nlcs/store-mode).");
        return;
      }

      // Compute balance from tickets scoped to this school
      const tq = query(
        collection(db, "schools", schoolId, "tickets"),
        where("studentId", "==", id)
      );
      const ticketSnap = await getDocs(tq);
      let balance = 0;
      ticketSnap.forEach((d) => {
        const t = d.data();
        balance += t.quantity ?? 1;
      });

      setStudent({
        ...studentData,
        id: studentData.uid || studentDoc.id,
        ticketBalance: balance,
      });
      setCart([]);
      setMessage("✅ Student loaded");
    } catch (error) {
      console.error("Failed to load student:", error);
      alert("Failed to load student: " + (error?.message || String(error)));
    }
  }

  async function loadItemToCart(idOrJson) {
    try {
      const { itemId, embeddedSchoolId } = parseScannedText(idOrJson);
      if (!itemId) {
        alert("Invalid item code");
        return;
      }

      if (!schoolId) {
        alert("No school context detected.");
        return;
      }

      const itemRef = doc(db, "schools", schoolId, "store", itemId);
      const itemSnap = await getDoc(itemRef);

      if (!itemSnap.exists()) {
        alert("Item not found in this school's store");
        return;
      }
      const item = itemSnap.data();

      // Only block if both sides have schoolIds and they truly differ.
      const itemSchool = item.schoolId || embeddedSchoolId || null;
      if (itemSchool && schoolId && itemSchool !== schoolId) {
        console.warn("School mismatch", { pageSchool: schoolId, itemSchool });
        alert(`This item belongs to "${itemSchool}", not "${schoolId}".`);
        return;
      }

      if (item.available === false) {
        alert("This item is not available.");
        return;
      }

      const cost = item.ticketCost ?? item.cost ?? 0;

      setCart((prev) => {
        // If you want quantities, fold identical items together:
        const found = prev.find((p) => p.id === itemId);
        if (found) {
          return prev.map((p) =>
            p.id === itemId ? { ...p, qty: (p.qty || 1) + 1 } : p
          );
        }
        return [...prev, { id: itemId, ...item, ticketCost: cost, qty: 1 }];
      });
      setMessage(`🛒 Added item '${item.name}' (${cost} tickets)`);
    } catch (error) {
      console.error("Error loading item to cart:", error);
      alert("Could not add item: " + (error?.message || String(error)));
    }
  }

  async function handleScanStudent() {
    try {
      const { Html5Qrcode, Html5QrcodeSupportedFormats } = await import("html5-qrcode");
      const scannerId = "qr-scanner";
      const existingScanner = document.getElementById(scannerId);
      if (existingScanner) existingScanner.remove();
  
      const container = document.createElement("div");
      container.id = scannerId;
      Object.assign(container.style, {
        position: "fixed",
        inset: "0",
        backgroundColor: "#000000cc",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      });
      container.innerHTML = `
        <div style="width: 95%; max-width: 520px; margin: 1rem; background:#fff; border-radius:12px; padding: 12px;">
          <div id="qr-reader" style="width: 100%; min-height: 420px;"></div>
          <div style="display:flex; gap:.5rem; justify-content:flex-end; margin-top:.75rem;">
            <button id="close-scanner" style="padding:.5rem 1rem;">Cancel</button>
          </div>
        </div>
      `;
      document.body.appendChild(container);
  
      const html5QrCode = new Html5Qrcode("qr-reader");
      const config = {
        fps: 10,
        // You can pass a number to make it square; library will center it.
        qrbox: 380,
        aspectRatio: 1.0,
        disableFlip: true,
        formatsToSupport: [Html5QrcodeSupportedFormats.QR_CODE]
      };
  
      // Helper to start with a specific camera
      async function startWithCamera(cameraId) {
        await html5QrCode.start(
          { deviceId: { exact: cameraId } },
          config,
          async (decodedText) => {
            // console.log("Decoded student QR:", decodedText);
            await html5QrCode.stop();
            html5QrCode.clear();
            document.body.removeChild(container);
  
            // Accept URL or raw ID
            let maybeUrl = null;
            try { maybeUrl = new URL(decodedText); } catch {}
            const id = maybeUrl?.searchParams.get("id")
                     || maybeUrl?.searchParams.get("student")
                     || decodedText.trim();
  
            const sidFromUrl = maybeUrl ? extractSchoolIdFromPath(maybeUrl.pathname) : null;
            if (sidFromUrl && sidFromUrl !== schoolId) {
              navigate(`/${sidFromUrl}/store-mode?id=${encodeURIComponent(id)}`);
              return;
            }
  
            await loadStudentById(id);
          }
        );
      }
  
      // Try to pick a back camera; fall back if needed
      const cameras = await Html5Qrcode.getCameras();
      if (!cameras || cameras.length === 0) {
        throw new Error("No cameras found");
      }
      // Heuristic: prefer a camera labeled 'back' or 'rear' on mobile
      const backCam =
        cameras.find(c => /back|rear/i.test(c.label)) ||
        cameras[cameras.length - 1];
  
      try {
        await startWithCamera(backCam.id);
      } catch {
        // Fallback to the first available
        await startWithCamera(cameras[0].id);
      }
  
      document.getElementById("close-scanner").onclick = async () => {
        await html5QrCode.stop();
        html5QrCode.clear();
        document.body.removeChild(container);
      };
    } catch (error) {
      console.error("❌ Scanner init failed:", error);
      alert("Camera access failed. Allow camera permissions and try again.");
      const fallback = prompt("Enter student ID manually:");
      if (fallback) {
        await loadStudentById(fallback.trim());
        setMessage("✅ Student loaded manually");
      }
    }
  }

  async function handleScanItem() {
    if (!student) return alert("Scan a student first");
    try {
      const { Html5Qrcode } = await import("html5-qrcode");
      const scannerId = "qr-item-scanner";
      const existingScanner = document.getElementById(scannerId);
      if (existingScanner) existingScanner.remove();
  
      const container = document.createElement("div");
      container.id = scannerId;
      Object.assign(container.style, {
        position: "fixed",
        top: "0",
        left: "0",
        width: "100%",
        height: "100%",
        backgroundColor: "#000000cc",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
      });
      container.innerHTML = `
        <div style="width: 90%; max-width: 400px; margin: 1rem auto;">
          <div id="qr-reader-item" style="width: 100%;"></div>
          <button id="close-item-scanner" style="margin-top: 1rem; padding: 0.5rem 1rem;">Cancel</button>
        </div>
      `;
      document.body.appendChild(container);
  
      const html5QrCode = new Html5Qrcode("qr-reader-item");
      const config = { fps: 10, qrbox: { width: 250, height: 250 } };
  
      await html5QrCode.start(
        { facingMode: "environment" },
        config,
        async (decodedText) => {
          await html5QrCode.stop();
          html5QrCode.clear();
          document.body.removeChild(container);
  
          // Try to treat the scan as a URL first, then fall back to raw ID
          let maybeUrl = null;
          try { maybeUrl = new URL(decodedText); } catch {}
  
          const itemId =
            maybeUrl?.searchParams.get("item") ||
            decodedText.trim();
  
          // If QR includes a /:schoolId/ namespace and it's different, navigate (preserve student)
          const sidFromUrl = maybeUrl ? extractSchoolIdFromPath(maybeUrl.pathname) : null;
          if (sidFromUrl && sidFromUrl !== schoolId) {
            const withStudent = student?.id ? `&id=${encodeURIComponent(student.id)}` : "";
            navigate(`/${sidFromUrl}/store-mode?item=${encodeURIComponent(itemId)}${withStudent}`);
            return;
          }
  
          await loadItemToCart(itemId);
        }
      );
  
      document.getElementById("close-item-scanner").onclick = async () => {
        await html5QrCode.stop();
        html5QrCode.clear();
        document.body.removeChild(container);
      };
    } catch (error) {
      console.error("❌ Item scanner init failed:", error);
      const fallback = prompt("Enter item ID manually:");
      if (fallback) {
        await loadItemToCart(fallback.trim());
        setMessage("🛒 Item added manually");
      }
    }
  }

  async function handleConfirmPurchase() {
    if (!student || cart.length === 0) return;
    if (!schoolId) {
      alert("No school context detected.");
      return;
    }

    setIsConfirming(true);
    try {
      const requestId = window.crypto?.randomUUID?.()
        || `${auth.currentUser?.uid || "store"}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const purchaseStoreItems = httpsCallable(functions, "purchaseStoreItems");
      const result = await purchaseStoreItems({
        schoolId,
        studentId: student.id,
        requestId,
        items: cart.map((item) => ({id: item.id, qty: item.qty || 1})),
      });
      const purchase = result.data;

      setLastTransaction({
        id: purchase.transactionId,
        studentId: student.id,
        total: purchase.total,
      });
      setCart([]);
      setStudent((prev) => ({ ...prev, ticketBalance: purchase.balance }));
      setMessage("✅ Purchase successful!");
    } catch (err) {
      console.error("Confirm purchase failed:", err);
      alert("Purchase failed: " + (err?.message || String(err)));
    } finally {
      setIsConfirming(false);
    }
  }

  async function handleUndo() {
    if (!lastTransaction) return;
    if (!schoolId) return;

    try {
      const undoStorePurchase = httpsCallable(functions, "undoStorePurchase");
      const result = await undoStorePurchase({
        schoolId,
        transactionId: lastTransaction.id,
      });

      setStudent((prev) => result.data.balance == null ? prev : ({
        ...prev,
        ticketBalance: result.data.balance,
      }));

      setLastTransaction(null);
      setMessage("⛔ Last transaction voided.");
    } catch (err) {
      console.error("Undo failed:", err);
      alert("Undo failed: " + (err?.message || String(err)));
    }
  }

  if (!authChecked) return <p>Loading...</p>;

  return (
    <div className="store-container">
      <h1>🏪 PBIS Store Mode</h1>

      {!schoolId && (
        <div style={{ background: "#fff7ed", border: "1px solid #fdba74", padding: "0.75rem", borderRadius: 8, marginBottom: "1rem" }}>
          No school context detected. Navigate via your school namespace, e.g. <code>/nlcs/store-mode</code>.
        </div>
      )}

      <div style={{ marginBottom: "1rem" }}>
        {!student && (
          <>
            <button onClick={handleScanStudent}>📷 Scan Student</button>
            <div style={{ marginTop: "0.5rem" }}>
  <input
    type="text"
    placeholder="🔍 Search student name..."
    value={searchTerm}
    onChange={(e) => handleSearchStudents(e.target.value)}
    style={{
      padding: "0.5rem",
      width: "250px",
      borderRadius: "6px",
      border: "1px solid #ccc"
    }}
  />

  {searchResults.length > 0 && (
    <div style={{
      background: "#fff",
      border: "1px solid #ddd",
      borderRadius: "6px",
      marginTop: "0.5rem",
      maxHeight: "200px",
      overflowY: "auto"
    }}>
      {searchResults.map((s) => (
        <div
          key={s.id}
          onClick={() => {
            loadStudentById(s.uid || s.id);
            setSearchTerm("");
            setSearchResults([]);
          }}
          style={{
            padding: "0.5rem",
            cursor: "pointer",
            borderBottom: "1px solid #eee"
          }}
        >
          {s.name} ({s.advisory || "No Advisory"})
        </div>
      ))}
    </div>
  )}
</div>
          </>
        )}
        {student && (
          <>
            <p>
              Student:{" "}
              <strong>
                {student.name} (Advisory: {getAdvisoryDisplayName(student.advisory)})
              </strong>{" "}
              | Tickets: {student.ticketBalance}
            </p>
            <div className="store-buttons">
              <button onClick={handleScanItem}>🛒 Scan Item</button>
              <button onClick={handleConfirmPurchase} disabled={cart.length === 0 || isConfirming}>
                {isConfirming ? "Processing..." : "✅ Confirm Purchase"}
              </button>
              <button onClick={handleUndo} disabled={!lastTransaction}>
                ⏪ Undo Last
              </button>
            </div>

            <div style={{ marginTop: "1.5rem" }}>
              <button
                onClick={() => {
                  setStudent(null);
                  setCart([]);
                  setMessage("");
                  setLastTransaction(null);
                }}
                style={{
                  backgroundColor: "#f59e0b",
                  color: "white",
                  padding: "0.5rem 1rem",
                  borderRadius: "6px",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                🔙 Return / Select Different Student
              </button>
            </div>
          </>
        )}
      </div>

      {cart.length > 0 && (
        <div>
          <h3>🗳 Cart:</h3>
          <ul className="cart-list">
  {cart.map((item, idx) => (
    <li key={idx}>
                {item.name} — {(item.ticketCost ?? item.cost ?? 0)} tickets
                {item.qty ? ` × ${item.qty}` : null}
                <button
                  onClick={() => {
                    setCart((prev) => {
                      const next = [...prev];
                      next.splice(idx, 1);
                      return next;
                    });
                  }}
                  style={{ marginLeft: "0.5rem" }}
                >
                  🗑 Remove
                </button>
              </li>
            ))}
          </ul>
          <p>
            Total:{" "}
            {cart.reduce(
              (acc, item) => acc + (item.ticketCost ?? item.cost ?? 0) * (item.qty || 1),
              0
            )}{" "}
            tickets
          </p>
        </div>
      )}

      {message && <p>{message}</p>}
    </div>
  );
};

export default StoreMode;
